import { ArrowDownUp, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { listings, offerings } from "@/mocks/residual";
import { formatDate, formatNumber, formatUsd } from "@/lib/format";
import type { ListingStatus } from "@/types/residual";
const STATUS_TONE: Record<ListingStatus, string> = {
  listed: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  negotiating: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  settled: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  cancelled: "bg-muted text-muted-foreground"
};
export function MarketplacePage({
  onSelectListing
}: {
  onSelectListing: (listingId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"price" | "recent">("recent");
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = listings.filter(l => l.status === "listed" || l.status === "negotiating").filter(l_0 => {
      if (!q) return true;
      const o = offerings.find(x => x.id === l_0.offeringId);
      return `${o?.title ?? ""} ${o?.location ?? ""} ${o?.tokenSymbol ?? ""}`.toLowerCase().includes(q);
    });
    return list.sort((a, b) => sort === "price" ? a.askingPriceUsd - b.askingPriceUsd : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [query, sort]);
  return <div className="space-y-5">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Marketplace</h1>
				<p className="text-sm text-muted-foreground">
					Buy positions held by other users — chat-based negotiation, on-chain
					escrow.
				</p>
			</div>

			<div className="flex gap-2">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
					<Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by asset…" className="pl-9" />
				</div>
				<button type="button" onClick={() => setSort(sort === "price" ? "recent" : "price")} className="inline-flex items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-muted">
					<ArrowDownUp className="size-3.5" />
					{sort === "price" ? "Price" : "Recent"}
				</button>
			</div>

			<div className="grid gap-3">
				{visible.length === 0 ? <div className="rounded-2xl border border-dashed p-10 text-center">
						<p className="text-sm text-muted-foreground">
							Nothing on the secondary market right now.
						</p>
					</div> : visible.map(l_1 => {
        const o_0 = offerings.find(x_0 => x_0.id === l_1.offeringId);
        if (!o_0) return null;
        const primaryDiscount = (l_1.askingPriceUsd - o_0.tokenPrice) / o_0.tokenPrice * 100;
        return <Card key={l_1.id} className="cursor-pointer transition-shadow hover:shadow-md" onClick={() => onSelectListing(l_1.id)}>
								<CardContent className="flex gap-3 pt-5">
									<div className="size-16 shrink-0 rounded-lg bg-cover bg-center" style={{
              backgroundImage: `url(${o_0.heroUrl})`
            }} />
									<div className="flex-1 space-y-1">
										<div className="flex items-start justify-between gap-2">
											<div className="text-sm font-semibold leading-tight">
												{o_0.title}
											</div>
											<span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_TONE[l_1.status]}`}>
												{l_1.status}
											</span>
										</div>
										<div className="flex items-center gap-2 text-xs text-muted-foreground">
											<span>by {l_1.sellerLabel}</span>
											<span>•</span>
											<span>{formatDate(l_1.createdAt)}</span>
										</div>
										<div className="flex items-baseline justify-between pt-0.5">
											<div>
												<div className="text-sm font-semibold tabular-nums">
													{formatUsd(l_1.askingPriceUsd)}{" "}
													<span className="text-xs font-normal text-muted-foreground">
														/ {o_0.tokenSymbol}
													</span>
												</div>
												<div className="text-xs text-muted-foreground">
													{formatNumber(l_1.tokensRemaining)} of{" "}
													{formatNumber(l_1.tokens)} available
												</div>
											</div>
											<Badge variant="outline" className={`rounded-full font-normal ${primaryDiscount < 0 ? "text-emerald-700 dark:text-emerald-400" : primaryDiscount > 0 ? "text-rose-700 dark:text-rose-400" : ""}`}>
												{primaryDiscount > 0 ? "+" : ""}
												{primaryDiscount.toFixed(1)}% vs primary
											</Badge>
										</div>
										{l_1.openOffers > 0 && <div className="pt-1 text-xs text-muted-foreground">
												{l_1.openOffers} open offer
												{l_1.openOffers === 1 ? "" : "s"}
											</div>}
									</div>
								</CardContent>
							</Card>;
      })}
			</div>
		</div>;
}